/**
 * The shipping address a purchase needs, which is exactly the set
 * `POST /api/ecommerce/stripe-checkout` takes.
 *
 * Registration already collects every one of these, so checkout prefills and offers an edit rather
 * than asking again.
 */
export interface ShippingAddress {
  firstName: string;
  lastName: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
}

/** The subset of chip contact info an address is built from. Everything is nullable upstream. */
export interface ContactInfoLike {
  firstName?: string | null;
  lastName?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  country?: string | null;
  primaryPhone?: string | null;
}

/**
 * Chip contact info calls the postcode `zipcode` and the phone `primaryPhone`; every address API
 * calls them `zip` and `phone`. This is the one place that mapping lives.
 *
 * Country defaults to US because registration hardcodes it and the plan this flow sells has no
 * non-USD price, so there is no other country to be in yet.
 */
export function shippingAddressFromContactInfo(contactInfo: ContactInfoLike | undefined): ShippingAddress {
  return {
    firstName: contactInfo?.firstName ?? '',
    lastName: contactInfo?.lastName ?? '',
    line1: contactInfo?.line1 ?? '',
    line2: contactInfo?.line2 ?? '',
    city: contactInfo?.city ?? '',
    state: contactInfo?.state ?? '',
    zip: contactInfo?.zipcode ?? '',
    country: contactInfo?.country || 'US',
    phone: contactInfo?.primaryPhone ?? '',
  };
}

/** A "1412 Bayview Ave, Kitsap, WA 98310" one-liner, used to tell two addresses apart. */
export function formatAddressLine(address: ShippingAddress): string {
  const street = [address.line1, address.line2].filter(Boolean).join(' ');
  const region = [address.city, address.state].filter(Boolean).join(', ');
  return [street, region, address.zip].filter(Boolean).join(', ');
}

/**
 * Whether there is enough here to be worth prefilling, used to choose between the registration and
 * Recurly addresses.
 *
 * Not a validity check: it only tests for non-empty, and registration validates none of these
 * fields. Stripe's `AddressElement` is the only thing that decides whether an address can be
 * purchased with -- gating a purchase on this instead lets a malformed address through to Recurly.
 */
export function isAddressComplete(address: ShippingAddress): boolean {
  return !!(address.line1.trim() && address.city.trim() && address.state.trim() && address.zip.trim());
}
